// Admin Settings → Etsy listing push → Stores: the go-live checklist and the
// "turn on sending" confirmation (plan P5; mockup approved by Volkan 2026-10-27).
// Steps tick themselves from the server state; step 4 ("I checked it on
// Etsy") is an admin's explicit confirmation.

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { formatDateTime } from '../../../lib/datetime'
import { toast } from '../../../lib/toast'
import { Button } from '../../../components/ui/button'
import { Card } from '../../../components/ui/card'
import { Dialog } from '../../../components/ui/dialog.lib'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog'
import { useT } from '../../../i18n'
import { etsyPushAdminI18n } from '../../../i18n/admin/etsy-push-admin'
import { GO_LIVE_KEY, postJson, useGoLive, type GoLive } from './etsy-pushes.lib'

type Step = { id: string; title: string; detail: string; done: boolean; action?: React.ReactNode }

export function EtsyGoLiveCard({ onShowSend }: { onShowSend?: (jobId: string) => void }) {
  const t = useT(etsyPushAdminI18n).goLive
  const qc = useQueryClient()
  const state = useGoLive()
  const confirm = useMutation({
    mutationFn: () => postJson<GoLive>('/admin/etsy-listing/go-live/confirm'),
    onSuccess: (data) => qc.setQueryData(GO_LIVE_KEY, data),
    onError: (err: Error) => toast.error(t.checkFailed, { description: err.message }),
  })
  const g = state.data
  if (!g) return <Card className="p-4" aria-busy={state.isLoading} />

  const e2e = g.end_to_end
  const steps: Step[] = [
    { id: 'code', title: t.codeLive, detail: t.codeLiveDetail, done: g.code_live },
    {
      id: 'test',
      title: t.testStore,
      detail: g.test_stores_on.length ? t.testStoreDetail(g.test_stores_on.join(', ')) : t.testStoreTodo,
      done: g.test_stores_on.length > 0,
    },
    {
      id: 'e2e',
      title: t.endToEnd,
      detail: e2e ? t.endToEndDetail(e2e.listing_title ?? '—') : t.endToEndTodo,
      done: !!e2e,
      action:
        e2e && onShowSend ? (
          <Button type="button" variant="outline" size="sm" onClick={() => onShowSend(e2e.job_id)}>
            {t.result}
          </Button>
        ) : undefined,
    },
    {
      id: 'check',
      title: t.check,
      detail:
        g.confirmed_at && g.confirmed_by_name
          ? t.checkedBy(g.confirmed_by_name, formatDateTime(g.confirmed_at))
          : t.checkDetail,
      done: !!g.confirmed_at,
      action:
        !g.confirmed_at && e2e ? (
          <Button type="button" size="sm" onClick={() => confirm.mutate()} disabled={confirm.isPending}>
            {t.checkButton}
          </Button>
        ) : undefined,
    },
    {
      id: 'sellers',
      title: t.sellers,
      detail:
        g.confirmed_at && g.seller_stores_on.length
          ? t.sellersDetail(g.seller_stores_on.join(', '))
          : t.sellersTodo,
      // Only after the check: a store on before it is a test store by definition.
      done: !!g.confirmed_at && g.seller_stores_on.length > 0,
    },
  ]
  const current = steps.findIndex((s) => !s.done)
  return (
    <Card className="space-y-2 p-4">
      <div>
        <h3 className="text-[15px] font-semibold text-[var(--color-text)]">{t.title}</h3>
        <p className="m-0 text-xs text-[var(--color-text-muted)]">{t.hint}</p>
      </div>
      <ol className="m-0 grid list-none p-0">
        {steps.map((s, i) => (
          <li
            key={s.id}
            aria-current={i === current ? 'step' : undefined}
            className="grid grid-cols-[30px_minmax(0,1fr)_auto] items-start gap-3 border-b border-[var(--color-border)] py-3 last:border-b-0"
          >
            <span
              aria-hidden="true"
              className={`grid h-[26px] w-[26px] place-items-center rounded-full text-[12.5px] font-bold ${
                s.done
                  ? 'bg-[var(--color-success)] text-[var(--color-surface)]'
                  : i === current
                    ? 'bg-[var(--color-text)] text-[var(--color-surface)]'
                    : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]'
              }`}
            >
              {s.done ? '✓' : i + 1}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-[var(--color-text)]">{s.title}</div>
              <div className="text-xs text-[var(--color-text-muted)]">{s.detail}</div>
            </div>
            {s.action ?? <span />}
          </li>
        ))}
      </ol>
    </Card>
  )
}

export function SwitchOnDialog({
  shop,
  open,
  checked,
  pending,
  onCancel,
  onConfirm,
}: {
  shop: string
  open: boolean
  checked: boolean
  pending: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const t = useT(etsyPushAdminI18n).confirm
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t.title(shop)}</DialogTitle>
          <DialogDescription asChild>
            <ul className="m-0 grid list-disc gap-1 pl-5 text-sm text-[var(--color-text)]">
              <li>{t.draftsOnly}</li>
              <li>{t.noCharge}</li>
              <li>{t.canTurnOff}</li>
            </ul>
          </DialogDescription>
        </DialogHeader>
        {!checked ? (
          <p className="m-0 text-xs text-[var(--color-text-muted)]">
            {t.notChecked} {t.testStoreNote}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel}>
            {t.cancel}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={pending}>
            {t.turnOn}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
